/**
 * Centralized persist seams for the Unit B edit suite (foundation §4.3),
 * wired to the DV-2221 asset-service endpoints (deployed with PR #146).
 *
 * Plain async functions (not hooks) so non-hook call paths keep working —
 * they dispatch the RTK Query mutations on the app store. Call sites own the
 * optimistic slice update AND the rollback: these functions REJECT on
 * failure, and every caller re-dispatches its pre-change value in `.catch()`.
 */
import { store } from '../../../store/store';
import { assetApi } from '../services/assetApi';

export interface PatchAssetPayload {
  assetUUID: string;
  caption?: string;
  hidden?: boolean;
}

export interface ReorderPhotosetPayload {
  photosetId: string;
  orderedAssetUUIDs: string[];
}

// DV-3537: every asset PATCH still in flight. Caption Sort reads captions and
// hidden flags server-side, so it must not POST while one of these is
// pending — a caption typed then immediately sorted would otherwise be
// sorted as uncaptioned (the slice already shows it, the server not yet).
const pendingAssetPatches = new Set<Promise<void>>();

/**
 * Resolves once every asset PATCH started before the call has settled,
 * successfully or not (a failed one has already rolled the slice back).
 */
export async function settlePendingAssetPatches(): Promise<void> {
  await Promise.allSettled([...pendingAssetPatches]);
}

// PATCH /asset-service/api/v1/assets/{assetUUID} — partial update; only the
// fields present in the body are applied (null/absent = unchanged), so build
// the body from the fields the caller actually passed.
export function persistAssetPatch(payload: PatchAssetPayload): Promise<void> {
  const request = sendAssetPatch(payload);
  pendingAssetPatches.add(request);
  const forget = () => pendingAssetPatches.delete(request);
  request.then(forget, forget);
  return request;
}

async function sendAssetPatch(payload: PatchAssetPayload): Promise<void> {
  const body: { caption?: string; hidden?: boolean } = {};
  if (payload.caption !== undefined) body.caption = payload.caption;
  if (payload.hidden !== undefined) body.hidden = payload.hidden;
  const sub = store.dispatch(
    assetApi.endpoints.updateAsset.initiate({
      assetUUID: payload.assetUUID,
      body
    })
  );
  try {
    await sub.unwrap();
  } finally {
    // Manual dispatch(...initiate()) has no hook lifecycle to reset the
    // cached mutation result on unmount, so it would otherwise accumulate
    // in state.assetApi.mutations forever across a long editing session.
    sub.reset();
  }
}

// PUT /asset-service/api/v1/assets/photosets/{photosetId}/order — the list
// must be an exact permutation of the photoset's current assetUUIDs
// (including non-photo Zillow url assets); see buildOrderedAssetUUIDs.
export async function persistPhotosetOrder(
  payload: ReorderPhotosetPayload
): Promise<void> {
  const sub = store.dispatch(
    assetApi.endpoints.reorderPhotoset.initiate(payload)
  );
  try {
    await sub.unwrap();
  } finally {
    // See persistAssetPatch above: manual initiate() needs an explicit
    // reset() to release the cached mutation result (no hook unmount).
    sub.reset();
  }
}
